import AddIcon from "@mui/icons-material/Add";
import AddPhotoAlternateOutlined from "@mui/icons-material/AddPhotoAlternateOutlined";
import ArrowBackOutlined from "@mui/icons-material/ArrowBackOutlined";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import MoreVertOutlined from "@mui/icons-material/MoreVertOutlined";
import StarOutlined from "@mui/icons-material/StarOutlined";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import { WebArtifacts, WebArtifactImage } from "../../types";
import { User, hostname, parseError } from "../../utils";
import LoadingButton from "../../components/LoadingButton";

interface WebArtifactsPanelProps {
  currentUser: User;
  companyId: string;
  getToken: () => Promise<string>;
  eventListener: (event: any) => void;
}

const WebArtifactsPanel: React.FC<WebArtifactsPanelProps> = ({
  currentUser,
  companyId,
  getToken,
  eventListener,
}) => {
  const [artifactsList, setArtifactsList] = useState<WebArtifacts[]>([]);
  const [selectedArtifacts, setSelectedArtifacts] = useState<WebArtifacts | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [globalStyles, setGlobalStyles] = useState("");
  const [headScripts, setHeadScripts] = useState("");
  const [brandGuidelines, setBrandGuidelines] = useState("");
  const [artifactName, setArtifactName] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuArtifacts, setMenuArtifacts] = useState<WebArtifacts | null>(null);
  const [editingImageIndex, setEditingImageIndex] = useState<number | null>(null);

  const fetchArtifactsList = useCallback(async () => {
    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/web-artifacts`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { companyId, listAll: true },
      });
      setArtifactsList(response.data.items || []);
    } catch (error) {
      eventListener({ type: "SET_ERROR", error: parseError(error) });
    } finally {
      setLoading(false);
    }
  }, [companyId, getToken, eventListener]);

  useEffect(() => {
    fetchArtifactsList();
  }, [fetchArtifactsList]);

  const handleSelectArtifacts = (artifacts: WebArtifacts) => {
    setSelectedArtifacts(artifacts);
    setGlobalStyles(artifacts.globalStyles || "");
    setHeadScripts(artifacts.headScripts || "");
    setBrandGuidelines(artifacts.brandGuidelines || "");
    setArtifactName(artifacts.name || "");
  };

  const handleCreateArtifacts = async () => {
    setCreating(true);
    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/web-artifacts`,
        {
          companyId,
          name: artifactName.trim() || "New Brand Kit",
          isDefault: artifactsList.length === 0,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setArtifactsList((prev) => [...prev, response.data]);
      setCreateDialogOpen(false);
      setArtifactName("");
      handleSelectArtifacts(response.data);
      eventListener({ type: "SET_SUCCESS", message: "Artifact set created" });
    } catch (error) {
      eventListener({ type: "SET_ERROR", error: parseError(error) });
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteArtifacts = async () => {
    if (!selectedArtifacts?.id) return;

    setDeleting(true);
    try {
      const token = await getToken();
      await axios.delete(`${hostname}/web-artifacts/${selectedArtifacts.id}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { companyId },
      });
      setArtifactsList((prev) => prev.filter((a) => a.id !== selectedArtifacts.id));
      setSelectedArtifacts(null);
      setDeleteDialogOpen(false);
      eventListener({ type: "SET_SUCCESS", message: "Artifact set deleted" });
    } catch (error) {
      eventListener({ type: "SET_ERROR", error: parseError(error) });
    } finally {
      setDeleting(false);
    }
  };

  const handleUpload = async (type: "favicon" | "logo" | "custom", file: File) => {
    if (!selectedArtifacts?.id) return;

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("companyId", companyId);
      formData.append("type", type);
      formData.append("name", file.name);
      formData.append("artifactsId", selectedArtifacts.id);

      const token = await getToken();
      const response = await axios.post(`${hostname}/web-artifacts/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      const uploadedImage: WebArtifactImage = response.data;

      setSelectedArtifacts((prev) => {
        if (!prev) return prev;
        const newArtifacts = { ...prev };
        if (type === "favicon" || type === "logo") {
          newArtifacts.artifacts = {
            ...newArtifacts.artifacts,
            [type]: uploadedImage,
          };
        } else {
          newArtifacts.artifacts = {
            ...newArtifacts.artifacts,
            images: [...(newArtifacts.artifacts.images || []), uploadedImage],
          };
        }
        return newArtifacts;
      });

      // Also update the list
      setArtifactsList((prev) =>
        prev.map((a) => {
          if (a.id !== selectedArtifacts.id) return a;
          const updated = { ...a };
          if (type === "favicon" || type === "logo") {
            updated.artifacts = { ...updated.artifacts, [type]: uploadedImage };
          } else {
            updated.artifacts = {
              ...updated.artifacts,
              images: [...(updated.artifacts.images || []), uploadedImage],
            };
          }
          return updated;
        })
      );

      eventListener({ type: "SET_SUCCESS", message: `${type} uploaded successfully` });
    } catch (error) {
      eventListener({ type: "SET_ERROR", error: parseError(error) });
    }
  };

  const handleSave = async () => {
    if (!selectedArtifacts) return;

    setSaving(true);
    try {
      const token = await getToken();
      const response = await axios.put(
        `${hostname}/web-artifacts`,
        {
          companyId,
          artifactsId: selectedArtifacts.id,
          artifacts: selectedArtifacts.artifacts,
          globalStyles,
          headScripts,
          brandGuidelines,
          name: artifactName,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSelectedArtifacts(response.data);
      setArtifactsList((prev) =>
        prev.map((a) => (a.id === selectedArtifacts.id ? response.data : a))
      );
      eventListener({ type: "SET_SUCCESS", message: "Web artifacts saved successfully" });
    } catch (error) {
      eventListener({ type: "SET_ERROR", error: parseError(error) });
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveImage = (type: "favicon" | "logo" | "custom", index?: number) => {
    setSelectedArtifacts((prev) => {
      if (!prev) return prev;
      const newArtifacts = { ...prev };
      if (type === "favicon" || type === "logo") {
        newArtifacts.artifacts = {
          ...newArtifacts.artifacts,
          [type]: null,
        };
      } else if (typeof index === "number") {
        newArtifacts.artifacts = {
          ...newArtifacts.artifacts,
          images: (newArtifacts.artifacts.images || []).filter((_, i) => i !== index),
        };
      }
      return newArtifacts;
    });
  };

  const handleUpdateImageDescription = (index: number, description: string) => {
    setSelectedArtifacts((prev) => {
      if (!prev) return prev;
      const newImages = [...(prev.artifacts.images || [])];
      newImages[index] = { ...newImages[index], description };
      return {
        ...prev,
        artifacts: {
          ...prev.artifacts,
          images: newImages,
        },
      };
    });
  };

  const FileUploadButton: React.FC<{
    type: "favicon" | "logo" | "custom";
    accept?: string;
    label: string;
  }> = ({ type, accept = "image/*", label }) => (
    <Button
      variant="outlined"
      component="label"
      startIcon={<AddPhotoAlternateOutlined />}
      sx={{ mt: 1 }}
    >
      {label}
      <input
        type="file"
        hidden
        accept={accept}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleUpload(type, file);
          e.target.value = "";
        }}
      />
    </Button>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  // Detail view when an artifact set is selected
  if (selectedArtifacts) {
    return (
      <Box sx={{ mt: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton onClick={() => setSelectedArtifacts(null)}>
              <ArrowBackOutlined />
            </IconButton>
            <TextField
              value={artifactName}
              onChange={(e) => setArtifactName(e.target.value)}
              variant="standard"
              placeholder="Brand Kit Name"
              sx={{ minWidth: 200 }}
            />
            {selectedArtifacts.isDefault && (
              <Chip label="Default" size="small" color="primary" icon={<StarOutlined />} />
            )}
          </Box>
          <Box display="flex" gap={1}>
            <Button
              color="error"
              onClick={() => setDeleteDialogOpen(true)}
              startIcon={<DeleteOutline />}
            >
              Delete
            </Button>
            <LoadingButton variant="contained" onClick={handleSave} loading={saving}>
              Save Changes
            </LoadingButton>
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Favicon */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Favicon
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  The small icon shown in browser tabs. Recommended size: 32x32 or 64x64 pixels.
                </Typography>
                {selectedArtifacts?.artifacts.favicon?.url ? (
                  <Box display="flex" alignItems="center" gap={2} mt={2}>
                    <Box
                      component="img"
                      src={selectedArtifacts.artifacts.favicon.url}
                      alt="Favicon"
                      sx={{ width: 32, height: 32, objectFit: "contain" }}
                    />
                    <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                      {selectedArtifacts.artifacts.favicon.name || "favicon"}
                    </Typography>
                    <Tooltip title="Remove">
                      <IconButton size="small" onClick={() => handleRemoveImage("favicon")}>
                        <DeleteOutline />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ) : (
                  <FileUploadButton
                    type="favicon"
                    accept="image/x-icon,image/png,image/svg+xml"
                    label="Upload Favicon"
                  />
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Logo */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Logo
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Your company logo used for Open Graph meta tags and branding.
                </Typography>
                {selectedArtifacts?.artifacts.logo?.url ? (
                  <Box display="flex" alignItems="center" gap={2} mt={2}>
                    <Box
                      component="img"
                      src={selectedArtifacts.artifacts.logo.url}
                      alt="Logo"
                      sx={{ width: 64, height: 64, objectFit: "contain" }}
                    />
                    <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                      {selectedArtifacts.artifacts.logo.name || "logo"}
                    </Typography>
                    <Tooltip title="Remove">
                      <IconButton size="small" onClick={() => handleRemoveImage("logo")}>
                        <DeleteOutline />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ) : (
                  <FileUploadButton type="logo" label="Upload Logo" />
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Custom Images */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Custom Images
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Additional images that can be used across landing pages. Add descriptions to help
                  the AI understand when and how to use each image.
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {(selectedArtifacts?.artifacts.images || []).map((image, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Box
                        sx={{
                          border: 1,
                          borderColor: "divider",
                          borderRadius: 1,
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            position: "relative",
                            width: "100%",
                            height: 120,
                          }}
                        >
                          <Box
                            component="img"
                            src={image.url}
                            alt={image.name || `Image ${index + 1}`}
                            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                          <IconButton
                            size="small"
                            sx={{
                              position: "absolute",
                              top: 4,
                              right: 4,
                              bgcolor: "background.paper",
                              "&:hover": { bgcolor: "background.paper" },
                            }}
                            onClick={() => handleRemoveImage("custom", index)}
                          >
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </Box>
                        <Box sx={{ p: 1.5 }}>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {image.name || `Image ${index + 1}`}
                          </Typography>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="Description for AI (e.g., 'Works well with dark backgrounds')"
                            value={image.description || ""}
                            onChange={(e) => handleUpdateImageDescription(index, e.target.value)}
                            sx={{ mt: 1 }}
                            multiline
                            rows={2}
                          />
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                  <Grid item xs={12} sm={6} md={4}>
                    <Box
                      sx={{
                        height: 200,
                        borderRadius: 1,
                        border: 2,
                        borderStyle: "dashed",
                        borderColor: "divider",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Button component="label" sx={{ width: "100%", height: "100%" }}>
                        <AddPhotoAlternateOutlined />
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleUpload("custom", file);
                            e.target.value = "";
                          }}
                        />
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Brand Guidelines */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Brand Guidelines
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Describe your brand's fonts, colors, and style preferences. This helps the AI create
                  landing pages that match your brand identity.
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={brandGuidelines}
                  onChange={(e) => setBrandGuidelines(e.target.value)}
                  placeholder={`Example:
- Primary font: Montserrat for headings, Open Sans for body text
- Primary color: #2563EB (blue), Secondary: #10B981 (green)
- Style: Modern, minimal, professional with rounded corners
- Tone: Friendly and approachable`}
                  sx={{ mt: 2 }}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Global Styles */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Global CSS (Advanced)
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Custom CSS that will be injected into landing pages using this brand kit.
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  value={globalStyles}
                  onChange={(e) => setGlobalStyles(e.target.value)}
                  placeholder={`:root {
  --brand-primary: #007bff;
  --brand-secondary: #6c757d;
}

/* Add your custom styles here */`}
                  sx={{ mt: 2, fontFamily: "monospace" }}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Head Scripts */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Head Scripts (Advanced)
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Scripts to inject into the {"<head>"} of landing pages. Use this for analytics
                  (Google Analytics, Meta Pixel, etc.), tracking codes, or other third-party integrations.
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  value={headScripts}
                  onChange={(e) => setHeadScripts(e.target.value)}
                  placeholder={`<!-- Google Analytics -->
<script async src="https://www.googletagmanager.com/gtag/js?id=G-XXXXXXXXXX"></script>
<script>
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}
  gtag('js', new Date());
  gtag('config', 'G-XXXXXXXXXX');
</script>

<!-- Meta Pixel Code -->
<script>
  !function(f,b,e,v,n,t,s)...
</script>`}
                  sx={{ mt: 2, fontFamily: "monospace" }}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Delete Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Delete Brand Kit</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete "{selectedArtifacts?.name}"? This action cannot be
              undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <LoadingButton
              variant="contained"
              color="error"
              onClick={handleDeleteArtifacts}
              loading={deleting}
            >
              Delete
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // List view
  return (
    <Box sx={{ mt: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Brand Kit</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Create Brand Kit
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage multiple brand kits (favicon, logo, images) for your landing pages. Each
        kit can be used by different landing pages for unique branding.
      </Typography>

      {artifactsList.length === 0 ? (
        <Card sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary" gutterBottom>
            No brand kits yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create your first brand kit to manage favicon, logo, and images for your landing
            pages.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Your First Brand Kit
          </Button>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {artifactsList.map((artifacts) => (
            <Grid item xs={12} sm={6} md={4} key={artifacts.id}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Typography variant="h6" noWrap sx={{ maxWidth: "70%" }}>
                      {artifacts.name || "Untitled"}
                    </Typography>
                    {artifacts.isDefault && (
                      <Chip
                        label="Default"
                        size="small"
                        color="primary"
                        icon={<StarOutlined />}
                      />
                    )}
                  </Box>
                  <Box display="flex" alignItems="center" gap={2} mt={2} sx={{ height: 32 }}>
                    {artifacts.artifacts.favicon?.url && (
                      <Tooltip title="Favicon">
                        <Box
                          component="img"
                          src={artifacts.artifacts.favicon.url}
                          alt="Favicon"
                          sx={{ width: 24, height: 24, objectFit: "contain" }}
                        />
                      </Tooltip>
                    )}
                    {artifacts.artifacts.logo?.url && (
                      <Tooltip title="Logo">
                        <Box
                          component="img"
                          src={artifacts.artifacts.logo.url}
                          alt="Logo"
                          sx={{ maxWidth: 48, maxHeight: 32, objectFit: "contain" }}
                        />
                      </Tooltip>
                    )}
                    {(artifacts.artifacts.images?.length || 0) > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
                        +{artifacts.artifacts.images?.length} images
                      </Typography>
                    )}
                  </Box>
                </CardContent>
                <Box sx={{ display: "flex", justifyContent: "flex-end", px: 1, pb: 1 }}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      setMenuAnchorEl(e.currentTarget);
                      setMenuArtifacts(artifacts);
                    }}
                  >
                    <MoreVertOutlined />
                  </IconButton>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => {
          setMenuAnchorEl(null);
          setMenuArtifacts(null);
        }}
      >
        <MenuItem
          onClick={() => {
            if (menuArtifacts) handleSelectArtifacts(menuArtifacts);
            setMenuAnchorEl(null);
            setMenuArtifacts(null);
          }}
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuArtifacts) {
              setSelectedArtifacts(menuArtifacts);
              setDeleteDialogOpen(true);
            }
            setMenuAnchorEl(null);
            setMenuArtifacts(null);
          }}
          sx={{ color: "error.main" }}
        >
          Delete
        </MenuItem>
      </Menu>

      {/* Create Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Brand Kit</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Name"
            value={artifactName}
            onChange={(e) => setArtifactName(e.target.value)}
            sx={{ mt: 1 }}
            placeholder="e.g., Main Brand, Campaign 2024"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <LoadingButton variant="contained" onClick={handleCreateArtifacts} loading={creating}>
            Create
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WebArtifactsPanel;
